import { useTranslation } from "react-i18next";
import { useMemo } from "react";

const DSP_ORDER_STATUS_OPTIONS = [
  {
    label: "Delivering",
    value: "DELIVERING",
  },
  {
    label: "Ended",
    value: "ENDED",
  },
];

const DSP_ORDER_STATE_OPTIONS = [
  {
    label: "Active",
    value: "ACTIVE",
  },
  {
    label: "Inactive",
    value: "INACTIVE",
  },
  {
    label: "Ended",
    value: "ENDED",
  },
];

const DSP_PRODUCT_CONVERSION_TYPE_OPTIONS = [
  {
    label: "Promoted",
    value: "Promoted",
  },
  {
    label: "Brand Halo",
    value: "Brand halo",
  },
];

// Static (English-only) filter configs. Kept for compatibility with
// `LIFECYCLE_FILTER_OPTIONS` in `merchandising/components/tables/filters.ts`,
// which collects filter metadata at module load. Use the `useDsp...` hooks
// below at the call site to get translated `selectOptions`.
export const DSP_ORDER_STATUS_FILTER = {
  label: "Status",
  id: "delivery_status",
  format: "select",
  selectOptions: [
    {
      value: "all",
      label: "All Statuses",
    },
    ...DSP_ORDER_STATUS_OPTIONS.map((status) => ({ label: status.label, value: status.value })),
  ],
  override: true,
  defaultValue: "all",
  operator: "custom",
  filterFunction: (order, filterValue) => {
    if (filterValue !== "all") {
      return order.delivery_status === filterValue;
    }
    return true;
  },
};

export const DSP_ORDER_STATE_FILTER = {
  label: "State",
  id: "order_state",
  format: "select",
  selectOptions: [
    {
      value: "all",
      label: "All States",
    },
    ...DSP_ORDER_STATE_OPTIONS.map((state) => ({ label: state.label, value: state.value })),
  ],
  override: true,
  defaultValue: "all",
  operator: "custom",
  filterFunction: (order, filterValue) => {
    if (filterValue !== "all") {
      return order.order_state === filterValue;
    }
    return true;
  },
};

export const DSP_PRODUCT_CONVERSION_TYPE_FILTER = {
  label: "Conversion Type",
  id: "conversion_type",
  format: "select",
  selectOptions: [
    {
      value: "all",
      label: "All Conversion Types",
    },
    ...DSP_PRODUCT_CONVERSION_TYPE_OPTIONS.map((status) => ({ label: status.label, value: status.value })),
  ],
  override: true,
  defaultValue: "all",
  operator: "custom",
  filterFunction: (order, filterValue) => {
    if (filterValue !== "all") {
      return order.conversion_type === filterValue;
    }
    return true;
  },
};

// Translated select-option lists for use as `columnFilterSelectOptions`
// values in dsp tables. The `label` shown in the cell formatter and filter
// chips is sourced from these translated options.
export const useDSPOrderStatusSelectOptions = () => {
  const { t } = useTranslation(["dsp"]);
  return useMemo(
    () => [
      { value: "all", label: t(($) => $.dsp["All Statuses"]) },
      { value: "DELIVERING", label: t(($) => $.dsp["Delivering"]) },
      { value: "ENDED", label: t(($) => $.dsp["Ended"]) },
    ],
    [t],
  );
};

export const useDSPOrderStateSelectOptions = () => {
  const { t } = useTranslation(["dsp"]);
  return useMemo(
    () => [
      { value: "all", label: t(($) => $.dsp["All States"]) },
      { value: "ACTIVE", label: t(($) => $.dsp["Active"]) },
      { value: "INACTIVE", label: t(($) => $.dsp["Inactive"]) },
      { value: "ENDED", label: t(($) => $.dsp["Ended"]) },
    ],
    [t],
  );
};

export const useDSPProductConversionTypeSelectOptions = () => {
  const { t } = useTranslation(["dsp"]);
  return useMemo(
    () => [
      { value: "all", label: t(($) => $.dsp["All Conversion Types"]) },
      { value: "Promoted", label: t(($) => $.dsp["Promoted"]) },
      { value: "Brand halo", label: t(($) => $.dsp["Brand Halo"]) },
    ],
    [t],
  );
};
